import React, { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { InputField } from "components/designV2/inputfield/inputfield";
import { UserRole, UserRoleRequest } from "model/Company";
import type {
  CompanyUser,
  CompanyUserRequestDto,
  StampClockUserRequestDto,
} from "model/Company";
import { useAlertStore } from "stores/alertStore/alertStore";
import { encryptPassword } from "utils/encrypt";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { validateEmail } from "web-apps/company/utils/utils";
import {
  isStampClockEmailValid,
  isStampClockPasswordValid,
  normalizeStampClockCredentials,
} from "../companyStampClock/stampClock.create.validator";
import {
  CompanyUserFormActions,
  CompanyUserFormFields,
  HiddenRadio,
  RadioControl,
  RadioGroup,
  RadioLabel,
  RadioOption,
} from "./companyUsers.styled";

type StandardProps = {
  readonly currentUser: CompanyUser | null;
  readonly mode?: "standard";
  readonly setCurrentUser: React.Dispatch<
    React.SetStateAction<CompanyUser | null>
  >;
  readonly setShowAddUserModal: React.Dispatch<React.SetStateAction<boolean>>;
};

type StampClockProps = {
  readonly mode: "stampClock";
  readonly onCancel: () => void;
  readonly onCreated: (email: string) => void;
  readonly onSubmittingChange?: (isSubmitting: boolean) => void;
};

type Props = StandardProps | StampClockProps;

type RoleOption = {
  readonly label: string;
  readonly value: UserRoleRequest;
};

function getDefaultRole(currentUser: CompanyUser | null): UserRoleRequest {
  switch (currentUser?.role) {
    case UserRole.COMPANY_OWNER:
      return UserRoleRequest.COMPANY_OWNER;
    case UserRole.COMPANY_TIMESTAMP:
      return UserRoleRequest.COMPANY_TIMESTAMP;
    case UserRole.COMPANY_ADMIN:
    default:
      return UserRoleRequest.COMPANY_ADMIN;
  }
}

export const CompanyUserForm: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [, , alertDispatch] = useAlertStore();
  const [companyState, companyActions] = useCompanyStore();
  const isStampClockMode = props.mode === "stampClock";
  const currentUser = isStampClockMode ? null : props.currentUser;
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState(currentUser?.firstName ?? "");
  const [isLoading, setIsLoading] = useState(false);
  const [lastName, setLastName] = useState(currentUser?.lastName ?? "");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [role, setRole] = useState<UserRoleRequest>(
    isStampClockMode
      ? UserRoleRequest.COMPANY_TIMESTAMP
      : getDefaultRole(currentUser),
  );
  const hasStampClockModule =
    companyState.companySettings?.find(
      (setting: { key: string; value: string }) =>
        setting.key === "CompanyShowStampclockModule",
    )?.value === "true";
  const isEmailValid = isStampClockMode
    ? isStampClockEmailValid(email)
    : validateEmail(email);
  const isEmailErrorVisible = Boolean(email) && !isEmailValid;

  useEffect(() => {
    setRole(
      isStampClockMode
        ? UserRoleRequest.COMPANY_TIMESTAMP
        : getDefaultRole(currentUser),
    );
  }, [currentUser, isStampClockMode]);

  const roleOptions: RoleOption[] = [
    {
      label: t("CompanyUsers.CompanyOwner"),
      value: UserRoleRequest.COMPANY_OWNER,
    },
    {
      label: t("CompanyUsers.CompanyAdmin"),
      value: UserRoleRequest.COMPANY_ADMIN,
    },
  ];

  if (hasStampClockModule) {
    roleOptions.push({
      label: t("CompanyUsers.CompanyStampClock"),
      value: UserRoleRequest.COMPANY_TIMESTAMP,
    });
  }

  const isFormValid = (): boolean => {
    if (isStampClockMode) {
      return isEmailValid && isStampClockPasswordValid(password);
    }
    if (currentUser) {
      return Boolean(firstName && lastName);
    }
    return Boolean(firstName && lastName && email && isEmailValid && role);
  };

  const setSubmitting = (isSubmitting: boolean): void => {
    setIsLoading(isSubmitting);
    if (props.mode === "stampClock") {
      props.onSubmittingChange?.(isSubmitting);
    }
  };

  const createStampClockUser = async (companyId: number): Promise<string> => {
    const credentials = normalizeStampClockCredentials({ email, password });
    const data: StampClockUserRequestDto = {
      email: credentials.email,
      provisionToken: await encryptPassword(
        credentials.password,
        credentials.email,
      ),
      role: UserRoleRequest.COMPANY_TIMESTAMP,
    };
    await companyActions.addCompanyUser(companyId, data);
    setPassword("");
    return credentials.email;
  };

  const createStandardUserRequest =
    async (): Promise<CompanyUserRequestDto> => {
      const provisionToken =
        role === UserRoleRequest.COMPANY_TIMESTAMP && password !== ""
          ? await encryptPassword(password, email)
          : "";
      return { email, firstName, lastName, provisionToken, role };
    };

  const submitStandardUser = async (companyId: number): Promise<void> => {
    if (props.mode === "stampClock") return;
    if (currentUser) {
      await companyActions.updateCompanyUser(
        companyId,
        currentUser.id,
        currentUser.firebaseId,
        role,
        firstName,
        lastName,
      );
    } else {
      await companyActions.addCompanyUser(
        companyId,
        await createStandardUserRequest(),
      );
    }
  };

  const showCreationError = (): void => {
    const messageKey = isStampClockMode
      ? "CompanyStampClock.CouldNotCreate"
      : currentUser
        ? "CompanyUsers.UserCouldNotBeUpdated"
        : "CompanyUsers.CouldNotAddUser";
    alertDispatch({
      type: "SHOW_ALERT",
      payload: {
        icon: "alert",
        message: t(messageKey),
        title: t("Alerts.OhNo"),
      },
    });
  };

  const completeSubmission = (createdEmail: string | null): void => {
    if (props.mode === "stampClock") {
      if (createdEmail !== null) props.onCreated(createdEmail);
      return;
    }
    if (currentUser) props.setCurrentUser(null);
    props.setShowAddUserModal(false);
  };

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (!companyState.company || !isFormValid() || isLoading) return;
    let createdEmail: string | null = null;
    let isSuccessful = false;
    setSubmitting(true);
    try {
      if (isStampClockMode) {
        createdEmail = await createStampClockUser(companyState.company.id);
      } else {
        await submitStandardUser(companyState.company.id);
      }
      isSuccessful = true;
    } catch {
      showCreationError();
    } finally {
      setSubmitting(false);
    }
    if (isSuccessful) completeSubmission(createdEmail);
  };

  const handleCancel = (): void => {
    if (props.mode === "stampClock") {
      props.onCancel();
      return;
    }
    props.setCurrentUser(null);
    props.setShowAddUserModal(false);
  };

  return (
    <CompanyUserFormFields
      aria-label={t(
        isStampClockMode
          ? "CompanyStampClock.CreateTitle"
          : currentUser
            ? "CompanyUsers.UpdateUser"
            : "CompanyUsers.AddUser",
      )}
      onSubmit={handleSubmit}
    >
      {!isStampClockMode && (
        <>
          <InputField
            label={t("CompanyUsers.FirstName")}
            value={firstName}
            onChange={setFirstName}
            fullWidth
          />
          <InputField
            label={t("CompanyUsers.LastName")}
            value={lastName}
            onChange={setLastName}
            fullWidth
          />
        </>
      )}
      {!currentUser && (
        <InputField
          label={t(
            isStampClockMode
              ? "CompanyStampClock.LoginEmail"
              : "CompanyUsers.Email",
          )}
          value={email}
          type="email"
          placeholder={
            isStampClockMode
              ? t("CompanyStampClock.EmailPlaceholder")
              : undefined
          }
          autoComplete={isStampClockMode ? "email" : undefined}
          disabled={isStampClockMode && isLoading}
          hasError={isEmailErrorVisible}
          errorText={t("CompanyUsers.InvalidEmail")}
          onChange={setEmail}
          fullWidth
        />
      )}
      {!currentUser && role === UserRoleRequest.COMPANY_TIMESTAMP && (
        <InputField
          label={t("General.Password")}
          value={password}
          type="password"
          placeholder={isStampClockMode ? "••••••••" : undefined}
          autoComplete={isStampClockMode ? "new-password" : undefined}
          disabled={isStampClockMode && isLoading}
          hasError={isStampClockMode && passwordError}
          errorText={t("RegisterCompany.PasswordMustBe6Characters")}
          onChange={(value) => {
            setPassword(value);
            setPasswordError(false);
          }}
          onBlur={() => {
            if (isStampClockMode) {
              setPasswordError(!isStampClockPasswordValid(password));
            }
          }}
          fullWidth
        />
      )}

      {!isStampClockMode && (
        <RadioGroup role="radiogroup" aria-label={t("CompanyUsers.Role")}>
          {roleOptions.map((option) => (
            <RadioOption key={option.value}>
              <HiddenRadio
                type="radio"
                name="company-user-role"
                value={option.value}
                checked={role === option.value}
                onChange={() => {
                  if (option.value !== UserRoleRequest.COMPANY_TIMESTAMP) {
                    setPassword("");
                  }
                  setRole(option.value);
                }}
              />
              <RadioControl />
              <RadioLabel>{option.label}</RadioLabel>
            </RadioOption>
          ))}
        </RadioGroup>
      )}

      <CompanyUserFormActions>
        <SecondaryButton
          label={t("General.Cancel")}
          disabled={isLoading}
          onClick={handleCancel}
        />
        <PrimaryButton
          label={t(
            isStampClockMode
              ? "CompanyStampClock.CreateButton"
              : "CompanyUsers.Save",
          )}
          type="submit"
          disabled={!isFormValid()}
          isLoading={isLoading}
        />
      </CompanyUserFormActions>
    </CompanyUserFormFields>
  );
};
